import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Award,
  Flame,
  Target,
  Clock,
  CheckCircle2,
  BookOpen,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';

interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: string;
  isEmailVerified: boolean;
  totalXp: number;
  level: number;
  streak: number;
  createdAt: string;
  avatarUrl?: string | null;
}

interface StatsOverview {
  sessions: {
    totalSessions: number;
    completedSessions: number;
    averageScore: number;
    totalTimeSpent: number;
    totalQuestions: number;
    totalCorrect: number;
    accuracy: number;
  };
  gamification: {
    totalXp: number;
    level: number;
    currentStreak: number;
    longestStreak: number;
    xpToNextLevel: number;
  };
  categoryPerformance: {
    categoryId: string;
    name: string;
    totalQuestions: number;
    correctAnswers: number;
    accuracy: number;
  }[];
}

interface SessionRow {
  id: string;
  type: string;
  status: string;
  examName: string;
  categoryName: string | null;
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  timeSpent: number;
  createdAt: string;
}

function formatDuration(seconds: number): string {
  if (!seconds) return '0 min';
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}min` : `${m} min`;
}

const TYPE_LABEL: Record<string, string> = {
  practice: 'Entraînement',
  simulation: 'Simulation',
  fullExam: 'Examen complet',
};

const STATUS_META: Record<string, { label: string; cls: string }> = {
  completed: { label: 'Terminée', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  in_progress: { label: 'En cours', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  abandoned: { label: 'Abandonnée', cls: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' },
};

function StatCard({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
        {icon}
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export function AdminUserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<AdminUser | null>(null);
  const [stats, setStats] = useState<StatsOverview | null>(null);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      adminApi.getUserById(id),
      adminApi.getUserStats(id).catch(() => ({ data: null })),
      adminApi.getUserSessions(id, 50).catch(() => ({ data: [] })),
    ])
      .then(([u, s, h]) => {
        if (cancelled) return;
        setUser(u.data);
        setStats(s.data);
        setSessions(h.data ?? []);
      })
      .catch(() => {
        if (!cancelled) toast.error("Impossible de charger le profil de l'utilisateur.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-20 text-gray-400">
        <p className="mb-4">Utilisateur introuvable.</p>
        <button onClick={() => navigate('/admin/users')} className="text-emerald-600 hover:underline">
          Retour à la liste
        </button>
      </div>
    );
  }

  const s = stats?.sessions;

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/admin/users')}
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
      >
        <ArrowLeft size={16} /> Retour aux utilisateurs
      </button>

      {/* En-tête profil */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 flex items-center gap-4">
        <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
          {user.name?.charAt(0)?.toUpperCase() ?? '?'}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white truncate">{user.name}</h1>
            {user.role === 'admin' && (
              <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 rounded">
                🛡 Admin
              </span>
            )}
            {user.isEmailVerified && (
              <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 rounded">
                Vérifié
              </span>
            )}
          </div>
          <p className="text-sm text-gray-400 truncate">{user.email}</p>
          <p className="text-xs text-gray-400 mt-1">
            Inscrit le {new Date(user.createdAt).toLocaleDateString('fr-FR')}
          </p>
        </div>
      </div>

      {/* Cartes stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={<Award size={14} />} label="XP" value={(stats?.gamification.totalXp ?? user.totalXp ?? 0).toLocaleString()} sub={`Niveau ${stats?.gamification.level ?? user.level}`} />
        <StatCard icon={<Flame size={14} />} label="Série" value={`${stats?.gamification.currentStreak ?? user.streak ?? 0} j`} sub={`Record : ${stats?.gamification.longestStreak ?? 0} j`} />
        <StatCard icon={<BookOpen size={14} />} label="Sessions" value={String(s?.totalSessions ?? 0)} sub={`${s?.completedSessions ?? 0} terminées`} />
        <StatCard icon={<Target size={14} />} label="Score moyen" value={`${Math.round(s?.averageScore ?? 0)}%`} sub={`Précision ${Math.round(s?.accuracy ?? 0)}%`} />
        <StatCard icon={<CheckCircle2 size={14} />} label="Bonnes réponses" value={String(s?.totalCorrect ?? 0)} sub={`sur ${s?.totalQuestions ?? 0} questions`} />
        <StatCard icon={<Clock size={14} />} label="Temps total" value={formatDuration(s?.totalTimeSpent ?? 0)} />
      </div>

      {/* Performance par catégorie */}
      {stats && stats.categoryPerformance.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <TrendingUp size={18} className="text-emerald-500" /> Performance par catégorie
          </h2>
          <div className="space-y-3">
            {stats.categoryPerformance
              .slice()
              .sort((a, b) => b.totalQuestions - a.totalQuestions)
              .map((c) => (
                <div key={c.categoryId}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-700 dark:text-gray-300">{c.name}</span>
                    <span className="text-gray-400">
                      {c.correctAnswers}/{c.totalQuestions} · {Math.round(c.accuracy)}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"
                      style={{ width: `${Math.min(100, Math.round(c.accuracy))}%` }}
                    />
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Historique des sessions */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white p-6 pb-3">
          Historique des sessions
        </h2>
        {sessions.length === 0 ? (
          <p className="px-6 pb-6 text-sm text-gray-400">Aucune session pour cet utilisateur.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-y border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Examen</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Score</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {sessions.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="px-6 py-3 text-sm">
                      <p className="font-medium text-gray-900 dark:text-white">{row.examName}</p>
                      {row.categoryName && <p className="text-xs text-gray-400">{row.categoryName}</p>}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-500">{TYPE_LABEL[row.type] ?? row.type}</td>
                    <td className="px-6 py-3 text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium">{Math.round(row.score)}%</span>
                      <span className="text-gray-400 text-xs ml-1">
                        ({row.correctAnswers}/{row.totalQuestions})
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_META[row.status]?.cls ?? 'bg-gray-100 text-gray-500'}`}>
                        {STATUS_META[row.status]?.label ?? row.status}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-500">
                      {new Date(row.createdAt).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
